// The "more" symbol list of the chart picker: filtering and keyboard handling, kept pure so they can be tested
// without a DOM. MorePicker.tsx renders it.

export interface MoreItem {
  sym: string;
  /** Company or fund name, without the " • Robinhood Token" suffix. */
  name: string;
}

/** Show a filter input above the list from this many symbols on. */
export const FILTER_FROM = 12;

export const shortName = (name: string | undefined, sym: string): string => (name ?? sym).replace(/\s*•\s*Robinhood Token$/i, "").trim() || sym;

/** Items whose symbol or name contains the query, case-insensitive; symbol-prefix matches first. */
export function filterMore(items: MoreItem[], query: string): MoreItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  const hits = items.filter((it) => it.sym.toLowerCase().includes(q) || it.name.toLowerCase().includes(q));
  const lead = (it: MoreItem) => (it.sym.toLowerCase().startsWith(q) ? 0 : 1);
  return hits.map((it, i) => ({ it, i })).sort((a, b) => lead(a.it) - lead(b.it) || a.i - b.i).map((x) => x.it);
}

export interface MoreState {
  open: boolean;
  /** Index of the highlighted row in the filtered list, -1 for none. */
  active: number;
  query: string;
}

export const CLOSED: MoreState = { open: false, active: -1, query: "" };

/** Open the list with the selected symbol highlighted, else the first row. */
export function openMore(items: MoreItem[], selected: string | null): MoreState {
  const i = selected ? items.findIndex((it) => it.sym === selected) : -1;
  return { open: true, active: i >= 0 ? i : items.length > 0 ? 0 : -1, query: "" };
}

export function setQuery(items: MoreItem[], query: string): MoreState {
  return { open: true, query, active: filterMore(items, query).length > 0 ? 0 : -1 };
}

export interface KeyResult {
  state: MoreState;
  /** The symbol chosen with this key, if any. */
  pick?: string;
  /** Move focus back to the trigger. */
  focusTrigger?: boolean;
  /** The key was handled: prevent its default action. */
  handled: boolean;
}

/**
 * One key press while the list is open. `space` is false while the filter input has focus, so a space types.
 * Enter (or Space) picks the highlighted row, Escape and Tab close; both return focus to the trigger.
 */
export function moreKey(state: MoreState, key: string, items: MoreItem[], space = true): KeyResult {
  const shown = filterMore(items, state.query);
  const n = shown.length;
  const at = (active: number): KeyResult => ({ state: { ...state, active }, handled: true });
  switch (key) {
    case "ArrowDown":
      return at(n === 0 ? -1 : Math.min(n - 1, state.active + 1));
    case "ArrowUp":
      return at(n === 0 ? -1 : Math.max(0, state.active - 1));
    case "Home":
      return at(n === 0 ? -1 : 0);
    case "End":
      return at(n - 1);
    case "PageDown":
      return at(n === 0 ? -1 : Math.min(n - 1, state.active + 8));
    case "PageUp":
      return at(n === 0 ? -1 : Math.max(0, state.active - 8));
    case " ":
    case "Enter": {
      if (key === " " && !space) return { state, handled: false };
      const it = shown[state.active];
      if (!it) return { state, handled: true };
      return { state: CLOSED, pick: it.sym, focusTrigger: true, handled: true };
    }
    case "Escape":
    case "Tab":
      return { state: CLOSED, focusTrigger: true, handled: true };
    default:
      return { state, handled: false };
  }
}
